/**
 * The swing's look, from the ability's own data (lane AZ, plan 23 row 50):
 * an authored line colour, else its TOML aura colour, else its school colour.
 * The reach is the melee cone the swing actually sweeps (ability → class →
 * global), so the drawn edge is where enemies stop getting hit.
 *
 * Shared by the caster's MeleeBehavior and the remote swing (plan 29 F5) —
 * content + class only, so an observer computes the same look.
 */
import { MELEE_SWING_CONFIG, MELEE_SWING_STYLES, type MeleeSwingStyle } from '../../../config/abilities/meleeSwing.config';
import { resolveMeleeCone, type MeleeConeProfile } from '../../../shared/logic/MeleeConeProfile';
import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';
import { ABILITY_SCHOOL_COLORS } from '../../../shared/constants/AbilitySchoolColors';

/** `#RRGGBB` → 0xRRGGBB, or undefined when absent/malformed. */
function hexColor(value: unknown): number | undefined {
  if (typeof value !== 'string' || !/^#[0-9a-f]{6}$/i.test(value)) return undefined;
  return parseInt(value.slice(1), 16);
}

export function meleeSwingLook(abilityId: string, heroClass: string | undefined, lineColor: number | undefined): {
  color: number; reach: number; style: MeleeSwingStyle; cone: MeleeConeProfile;
} {
  const data = getAbilityData(abilityId) as { school?: string; vfx?: { auraColor?: string } } | undefined;
  const color = lineColor
    ?? hexColor(data?.vfx?.auraColor)
    ?? (data?.school ? ABILITY_SCHOOL_COLORS[data.school] : undefined)
    ?? MELEE_SWING_CONFIG.fallbackColor;
  const cone = resolveMeleeCone(abilityId, heroClass);
  return {
    color,
    reach: cone.rangePx,
    style: MELEE_SWING_STYLES[abilityId] ?? 'smear',
    cone,
  };
}
